import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQuery } from '@tanstack/react-query';
import { createApiClient } from '../../api/client';
import { useAuth } from '../../auth/useAuth';

const ROLE_OPTIONS = [
  { value: 'protagonist', label: 'Protagonist' },
  { value: 'antagonist', label: 'Antagonist' },
  { value: 'supporting', label: 'Supporting' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'love_interest', label: 'Love Interest' },
  { value: 'sidekick', label: 'Sidekick' },
  { value: 'comic_relief', label: 'Comic Relief' },
  { value: 'foil', label: 'Foil' },
  { value: 'narrator', label: 'Narrator' },
  { value: 'other', label: 'Other' },
] as const;

interface CharacterItem {
  id: string;
  entityId: string;
  entityName?: string | null;
  role?: string | null;
  customRoleName?: string | null;
  goal?: string | null;
  motivation?: string | null;
  stakes?: string | null;
  arcSummary?: string | null;
}

interface EntityOption {
  id: string;
  name: string;
  entityType: string;
}

interface CharacterSectionProps {
  characters: CharacterItem[];
  onAdd: (entityId: string, role?: string) => void;
  onRemove: (id: string) => void;
  onUpdateRole?: (id: string, role: string) => void;
  bookId?: string;
}

export function CharacterSection({
  characters,
  onAdd,
  onRemove,
  onUpdateRole,
  bookId,
}: CharacterSectionProps) {
  const { getToken } = useAuth();
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(
    null,
  );
  const [role, setRole] = useState('protagonist');

  const { data: entities = [] } = useQuery<EntityOption[]>({
    queryKey: ['entities-for-picker', bookId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/entities')
        .json<{ data: EntityOption[] }>();
      return resp.data ?? [];
    },
  });

  const existingIds = new Set(characters.map((c) => c.entityId));
  const available = entities.filter((e) => !existingIds.has(e.id));

  const getRoleLabel = (role: string | null | undefined) => {
    if (!role) return null;
    const opt = ROLE_OPTIONS.find((o) => o.value === role);
    return opt?.label ?? role;
  };

  return (
    <Accordion defaultExpanded={characters.length > 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">
          Characters ({characters.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {characters.map((c) => (
            <Box
              key={c.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {c.entityName ?? 'Unknown'}
              </Typography>
              {onUpdateRole ? (
                <FilterableSelect
                  options={ROLE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  value={c.role ?? 'supporting'}
                  onChange={(newValue) => onUpdateRole(c.id, newValue)}
                  allowEmpty={false}
                  size="small"
                  sx={{ width: 140 }}
                />
              ) : (
                c.role && (
                  <Chip
                    label={getRoleLabel(c.role)}
                    size="small"
                    variant="outlined"
                  />
                )
              )}
              <IconButton
                size="small"
                onClick={() => onRemove(c.id)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              options={available}
              getOptionLabel={(o) => o.name}
              value={selectedEntity}
              onChange={(_, v) => setSelectedEntity(v)}
              renderInput={(params) => (
                <TextField {...params} label="Add character" />
              )}
              sx={{ flex: 1 }}
            />
            <FilterableSelect
              options={ROLE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={role}
              onChange={(newValue) => setRole(newValue)}
              label="Role"
              allowEmpty={false}
              size="small"
              sx={{ width: 150 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={!selectedEntity}
              onClick={() => {
                if (selectedEntity) {
                  onAdd(selectedEntity.id, role || undefined);
                  setSelectedEntity(null);
                  setRole('protagonist');
                }
              }}
            >
              Add
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export { ROLE_OPTIONS };
